import AuthService from "../services/auth-service";
import logger from "./logger";

const host = process.env.API_HOST;
const port = process.env.API_PORT;
const fetchProxy = timedFetch(Number(process.env.REQUEST_TIMEOUT), onAbort);

export const baseUrl = ((port && ![80, 443].includes(Number(port))) ? [host, port] : [host]).join(":");
//export const baseUrl = (port ? [host, port] : [host]).join(":");
//export const baseUrl = "/api"; // Use this if we want to proxy requests in development
export { fetchProxy as fetchWithTimeout };

export const HTTP_STATUS_CODES = { 
  UNAUTHORIZED : 401,
  FORBIDDEN    : 403,
  NOT_FOUND    : 404,  
  SERVER_ERROR : 500,
};

export function isServerDownError(error: { message: string }) {
  // Return true if the server is (temporarily) down.
  return error.message === "Failed to fetch";
}

function onAbort(url: string, requestData: RequestInit, timeout: number) {
  const { method = "GET" /*, headers, body*/ } = requestData;

  if(process.env.NODE_ENV === "development") {
    console.log(
      `HTTP ${method.toUpperCase()} request to ${url} aborted after ${timeout} seconds.`
    );
  }

  logger.warn(`HTTP ${method.toUpperCase()} request to ${url} aborted after ${timeout} seconds.`);
}

/**
 * Create a timed fecth:
 *   (a fetch that aborts the request after a specified number of seconds have passed).
 *
 * @param {number} timeout (in seconds)
 * @param {Function} onAbort:
 *    A function to invoke when the fetch aborts due to timeout.
 *    The handler is auto-passed the arguments to the `fetch` call.
 * @return A function
 *   that takes the arguments that `fetch` takes
 *   returns the same Promise that `fetch` returns.
 */
function timedFetch(timeout = 0, onAbort?: (url: string, requestData: RequestInit, timeout: number) => void) {
  const controller   = new AbortController();
  const signal       = controller.signal;
  const abortHandler = typeof onAbort === "function" ? onAbort : () => {};

  return async function proxyFetch(url: string, options?: RequestInit): Promise<Response> {
    let timerId: NodeJS.Timeout | undefined;

    // Send the fetch request,
    const serverResponse = fetch(url, options);

    if(typeof timeout === "number" && timeout > 0) {
      // Setup the timer to cancel the request when the timeout expires.
      timerId = setTimeout(function abortRequest() {
        controller.abort();
      }, timeout * 1000);

      signal.addEventListener("abort", function handleRequestTimeout() {
        abortHandler(url, options ?? {}, timeout);
      });
    }

    let response = await serverResponse;

    response = await autoHandleExpiredAccessToken({ response, url, options });

    // Clear the timeout once our fetch returns before timeout expiry.
    if(timerId) {
      clearTimeout(timerId);
    }

    return response;
  };
}

// Automatically handle refresh tokens.
async function autoHandleExpiredAccessToken(data: { response: Response, url: string, options?: RequestInit }): Promise<Response> { 
  let response = data.response;
  const { url, options = {}} = data;

  // Check for token expiration (401 Unauthorized)
  if(response.status ===  HTTP_STATUS_CODES.UNAUTHORIZED) {
    try {
      // Hit the refresh endpoint to fetch a new access token
      const userStillLoggedIn = await AuthService.refreshAccessToken();

      if(userStillLoggedIn) { 
        // Retry the original request with the new access token
        options.headers = options.headers ?? {};
        options.headers["Authorization"] = AuthService.getBearerToken();
        response = await fetch(url, options);
      }
    } catch(error) {
      logger.error("autoHandleExpiredAccessToken error: ", error);
    }
  }

  return response;
}
